/**
 * FeatureToggleService
 * Centralized service for managing feature toggle states. The primary axis is
 * the user's onboarding step; secondary axes (foundation completion, current
 * session mode) are AND-ed in via dedicated resolvers (see
 * `_resolveUpgradePrompts`, `_resolvePrivacyButton`, and
 * `_resolveHeaderOptionsMenu`) so each toggle has a
 * single, predictable derivation.
 *
 * Features controlled:
 * - privacyButton: Privacy entry eligibility inside the chat menu
 * - headerOptionsMenu: Chat-header kebab visibility
 * - chatInputLayout: Chat input layout (simple vs full)
 * - upgradePrompts: Upgrade/premium conversion UI (coordinates with UpgradeService)
 * - plusButton: Plus/attachment button in chat input
 * - sidebarHistory: Sidebar chat history button visibility
 *
 * Usage:
 *   const service = new FeatureToggleService(eventBus);
 *   service.initialize();
 *
 *   // Programmatic control
 *   service.set('chatInputLayout', 'simple');
 *
 *   // Configure from onboarding step
 *   service.configureFromOnboardingStep('welcome');
 */

import { CONTENT_ACTIVATED } from '../core/contentSync.js';

/**
 * @typedef {'simple' | 'full'} ChatInputLayoutState
 */

/**
 * @typedef {Object} FeatureToggleState
 * @property {boolean} privacyButton - Privacy button visibility
 * @property {boolean} headerOptionsMenu - Chat-header options visibility
 * @property {ChatInputLayoutState} chatInputLayout - Chat input layout mode
 * @property {boolean} upgradePrompts - Upgrade prompts enabled (coordinates with UpgradeService)
 * @property {boolean} plusButton - Plus/attachment button visibility
 * @property {boolean} sidebarHistory - Sidebar chat history button visibility
 * @property {boolean} closeButton - Header close-orientation button visibility
 */

export class FeatureToggleService {
  /**
   * @param {Object} eventBus - Event bus for component communication
   */
  constructor(eventBus) {
    this.eventBus = eventBus;

    // Tracks whether the foundation module has been completed. Combined with
    // onboarding-step gating to decide whether upgrade prompts may surface.
    // Seeded from window.app.currentUser on initialize() and re-read on
    // user:updated, which `handleGuestConversion` emits with the registration
    // response (there is no /auth/me refetch on that path).
    this.foundationCompleted = false;

    // One-shot UX hold: when orientation completes, the post-orientation advice
    // session keeps the simple chat input. Released the first time the user
    // leaves that session (new chat, mode switch, or page navigation).
    this._orientationAdviceLockActive = false;
    this._orientationAdviceSessionSeen = false;

    // Current session mode (from ModeOrchestrator). Treated as a second axis
    // alongside the onboarding step when resolving privacy/menu eligibility — DISCOVERY
    // is fundamentally incompatible with privacy mode (its purpose is to
    // gather and persist user information). See `_resolvePrivacyButton`.
    this.currentModeName = null;

    // The most recently applied base config. Cached so we can re-resolve when
    // a non-step axis (mode) changes without needing to reconstruct it.
    this._lastConfig = null;

    /** @type {FeatureToggleState} */
    this.state = {
      privacyButton: true,
      headerOptionsMenu: false,
      plusButton: true,
      chatInputLayout: 'full',
      upgradePrompts: false,  // Derived from onboarding + foundationCompleted; see _resolveUpgradePrompts
      sidebarHistory: true,
      closeButton: false       // Header close button (orientation-only affordance)
    };

    // Onboarding step to toggle configuration mapping.
    // `upgradePrompts` here is the *step-level* allowance — the final value is
    // AND'd with `foundationCompleted` in `_resolveUpgradePrompts`.
    // `closeButton: true` during orientation surfaces the header exit affordance.
    this.stepConfigs = {
      'welcome': { privacyButton: false, plusButton: false, chatInputLayout: 'simple', upgradePrompts: false, sidebarHistory: false, closeButton: true },
      'name': { privacyButton: false, plusButton: false, chatInputLayout: 'simple', upgradePrompts: false, sidebarHistory: false, closeButton: true },
      'initial-chat': { privacyButton: false, plusButton: false, chatInputLayout: 'simple', upgradePrompts: false, sidebarHistory: false, closeButton: true },
      'quiz': { privacyButton: false, plusButton: false, chatInputLayout: 'simple', upgradePrompts: false, sidebarHistory: false, closeButton: true },
      'insight-preview': { privacyButton: false, plusButton: false, chatInputLayout: 'simple', upgradePrompts: false, sidebarHistory: false, closeButton: true }
    };

    // Default config (fully unlocked post-orientation). `upgradePrompts: true`
    // is the step-level allowance; the effective value still requires
    // `foundationCompleted` (see `_resolveUpgradePrompts`).
    this.defaultConfig = { privacyButton: true, plusButton: true, chatInputLayout: 'full', upgradePrompts: true, sidebarHistory: true, closeButton: false };

    // Bind methods
    this.handleUserUpdated = this.handleUserUpdated.bind(this);
    this.handleModeChanged = this.handleModeChanged.bind(this);
    this.handleModeRestored = this.handleModeRestored.bind(this);
    this.handleModeCleared = this.handleModeCleared.bind(this);
    this.handleOrientationDismissed = this.handleOrientationDismissed.bind(this);
    this.handleContentActivated = this.handleContentActivated.bind(this);
  }

  /**
   * The single "orientation is on screen right now" predicate. This is a *live*
   * state (P2), not the historical `onboardingStep`: a user who exits
   * orientation early must be unlocked in-session, so visibility keys on whether
   * the orientation UI is actually up — either a pending bootstrap orientation
   * (before ModeOrchestrator has a mode) or the ORIENTATION mode itself.
   *
   * @private
   * @returns {boolean}
   */
  _resolveOrientationLive() {
    return !!window.app?._pendingOrientation || this.currentModeName === 'ORIENTATION';
  }

  /**
   * Resolve sidebar content-navigation visibility. This is orthogonal to the
   * orientation chrome lockdown, so it lives outside the `stepConfigs`/
   * `_applyConfig` machinery: it is a pure function of the live orientation
   * predicate, the authoritative `hasContent` flags from `/auth/me` (kept on
   * `currentUser`), and `coupleId`. Every item is hidden while orientation is
   * on screen; otherwise each content item appears iff its page has content
   * (Discovery is always available, Notes keys on partner linkage).
   *
   * An absent/partial `hasContent` (guest, legacy, or an isolated probe
   * failure) resolves each content item to hidden — the safe default.
   *
   * @private
   * @returns {{discovery: boolean, practices: boolean, library: boolean, search: boolean, notes: boolean}}
   */
  _resolveSidebarContent() {
    const orientationLive = this._resolveOrientationLive();
    const currentUser = window.app?.currentUser;
    const hasContent = currentUser?.hasContent;
    return {
      discovery: !orientationLive,
      practices: !orientationLive && !!hasContent?.practices,
      library: !orientationLive && !!hasContent?.library,
      search: !orientationLive && !!hasContent?.search,
      notes: !orientationLive && !!currentUser?.coupleId
    };
  }

  /**
   * Resolve and emit the single `toggle:sidebarContent` event carrying the five
   * content-visibility booleans. Called from every site whose change could flip
   * a content item: init, each mode-lifecycle reapply, `content:activated`, and
   * user updates. Kept as one choke point so the resolution stays DRY.
   * @private
   */
  _emitSidebarContent() {
    this.eventBus.emit('toggle:sidebarContent', this._resolveSidebarContent());
  }

  /**
   * Resolve the effective `upgradePrompts` value by AND-ing the step-level
   * allowance with `foundationCompleted`. This is the single choke point that
   * enforces: upgrade UI is only offered once orientation *and* foundation are
   * both complete.
   *
   * @private
   * @param {Object} config - Step config providing a step-level allowance
   * @returns {boolean}
   */
  _resolveUpgradePrompts(config) {
    return !!config.upgradePrompts && this.foundationCompleted;
  }

  /**
   * Resolve the effective `privacyButton` value. The step-level allowance is
   * AND-ed with "not currently in DISCOVERY or INTAKE mode" — both modes are
   * inherently identifying (discovery gathers/persists user info; intake is
   * the onboarding entry that seeds Foundation), so privacy mode is
   * contextually nonsensical. Also suppressed while the post-orientation
   * advice-session hold is active, so the affection-patterns conversation
   * doesn't surface a privacy affordance the user hasn't earned context for
   * yet. This mirrors `_resolveUpgradePrompts`'s second-axis pattern.
   *
   * @private
   * @param {Object} config
   * @returns {boolean}
   */
  _resolvePrivacyButton(config) {
    if (this._orientationAdviceLockActive) return false;
    const mode = this.currentModeName;
    return !!config.privacyButton && mode !== 'DISCOVERY' && mode !== 'INTAKE';
  }

  /** Resolve the chat kebab for supported post-onboarding conversation modes. */
  _resolveHeaderOptionsMenu(config) {
    if (this._orientationAdviceLockActive || !config.privacyButton) return false;
    return ['ADVICE', 'TREATMENT'].includes(this.currentModeName);
  }

  /**
   * Apply a step/default config to `this.state`, resolving derived fields.
   * Caches the base config so non-step axes (mode) can trigger a re-resolve
   * without reconstructing it.
   *
   * @private
   * @param {Object} config
   */
  _applyConfig(config) {
    this._lastConfig = config;
    this.state = {
      ...config,
      upgradePrompts: this._resolveUpgradePrompts(config),
      privacyButton: this._resolvePrivacyButton(config),
      headerOptionsMenu: this._resolveHeaderOptionsMenu(config)
    };
  }

  /**
   * Initialize the service
   * Sets up event listeners and emits initial state
   * Checks current user state to apply orientation restrictions if needed
   */
  initialize() {
    // Seed foundation-completion from the current user before computing any state
    const currentUser = window.app?.currentUser;
    this.foundationCompleted = !!currentUser?.foundationCompleted;

    // Seed current mode from ModeOrchestrator (instantiated earlier in app.js)
    // so a page reload while in DISCOVERY resolves privacyButton correctly on
    // the first emit.
    this.currentModeName = window.modeOrchestrator?.currentMode?.name || null;

    // Listen for user updates that may include onboarding step changes
    this.eventBus.on('user:updated', this.handleUserUpdated);
    this.eventBus.on('user:onboarding-step-changed', this.handleUserUpdated);

    // Listen for mode transitions to reapply orientation state. All four
    // lifecycle edges are observed because leaving orientation does NOT emit
    // `mode:changed`: `returnToDashboard()` emits `mode:cleared`, and the ✕
    // affordance additionally emits `orientation:dismissed`. Without observing
    // these, a user who exits orientation early would stay locked until reload —
    // the exact bug this predicate fix targets.
    this.eventBus.on('mode:changed', this.handleModeChanged);
    this.eventBus.on('mode:restored', this.handleModeRestored);
    this.eventBus.on('mode:cleared', this.handleModeCleared);
    this.eventBus.on('orientation:dismissed', this.handleOrientationDismissed);

    // Optimistic in-session Practices reveal: an accepted draft practice flips
    // to active and emits `content:activated`. We durably cache the fact and
    // re-emit content visibility so Practices appears without a reload.
    this.eventBus.on(CONTENT_ACTIVATED, this.handleContentActivated);

    // Listen for orientation completion to restore normal state
    this.eventBus.on('orientation:completed', () => {
      console.log('[FeatureToggleService] Orientation completed, restoring normal state');
      this.configureForNormalMode();
    });

    // Release the post-orientation simple-input hold once the user leaves the
    // advice session. The first `session:started` after `orientation:completed`
    // is the advice session itself, so we skip it.
    this.eventBus.on('session:started', () => {
      if (!this._orientationAdviceLockActive) return;
      if (!this._orientationAdviceSessionSeen) {
        this._orientationAdviceSessionSeen = true;
        return;
      }
      this._releaseOrientationAdviceLock();
    });
    this.eventBus.on('page:changed', () => {
      if (this._orientationAdviceLockActive) this._releaseOrientationAdviceLock();
    });

    // Check if user is currently in orientation and apply restrictive state immediately
    // This ensures the correct UI state on app startup before any events fire
    this._applyInitialOrientationState();

    // Emit initial state to all listening components
    this.emitStateChanged();

    // Emit the initial content-navigation visibility (orthogonal to the chrome
    // state above — see `_resolveSidebarContent`).
    this._emitSidebarContent();

    console.log('[FeatureToggleService] Initialized with state:', this.state);
  }

  /**
   * Apply restrictive state if user is in orientation
   * Called during initialization to ensure correct state on startup
   * @private
   */
  _applyInitialOrientationState() {
    // Lockdown vs default keys on the *live* predicate, not the historical
    // `onboardingStep`. A user who exited orientation early has a phase-value
    // `onboardingStep` but is not live, so they fall through to the default
    // (unlocked) config instead of being stuck in lockdown until reload.
    if (this._resolveOrientationLive()) {
      const onboardingStep = window.app?.currentUser?.onboardingStep;
      // Prefer the step-specific lockdown config when we have a known step;
      // otherwise (pending bootstrap orientation, unknown step) the hardcoded
      // orientation config is the most restrictive fallback.
      if (onboardingStep !== undefined && onboardingStep !== null && onboardingStep !== 'complete') {
        console.log('[FeatureToggleService] Orientation live, step:', onboardingStep);
        this._applyConfig(this._resolveLockdownConfig(onboardingStep));
      } else {
        console.log('[FeatureToggleService] Orientation live (pending), applying restrictive state');
        this.configureForOrientation();
      }
      return;
    }

    // Not live: fully unlocked. Falls through to the default config so
    // upgradePrompts reflects the current `foundationCompleted` value.
    this._applyConfig(this.defaultConfig);
  }

  /**
   * Resolve which lockdown config to apply for a given non-'complete' step.
   * Known lowercase legacy step keys ('welcome', 'name', 'quiz', etc.) map to
   * their entry in `stepConfigs`. Any unknown value — including uppercase phase
   * names from `OrientationMode` (`DISCOVERY`, `INSIGHT`, `ACCOUNT_PROMPT`,
   * `TRANSITION`) — falls through to the most restrictive 'welcome' config,
   * NEVER to `defaultConfig`. This is the single guard that prevents an unknown
   * step from accidentally unlocking the UI for a dismissed-but-incomplete user.
   *
   * @private
   * @param {string} step
   * @returns {Object} A step config (never `defaultConfig`)
   */
  _resolveLockdownConfig(step) {
    return this.stepConfigs[step] || this.stepConfigs['welcome'];
  }

  /**
   * Configure state for orientation mode
   * Hides history, plus button, privacy button; keeps input simple. (Sidebar
   * content-navigation visibility is owned separately by `_resolveSidebarContent`.)
   */
  configureForOrientation() {
    this._applyConfig({
      privacyButton: false,
      plusButton: false,
      chatInputLayout: 'simple',
      upgradePrompts: false,
      sidebarHistory: false,
      closeButton: true
    });
  }

  /**
   * Configure state for post-orientation (normal state)
   * Restores all features except the chat input (stays simple) and the
   * privacy button (stays hidden) for the immediate post-orientation advice
   * session. Both holds are released together by
   * `_releaseOrientationAdviceLock` when the user leaves that session.
   *
   * The lock flag is set before `_applyConfig` so `_resolvePrivacyButton` sees
   * it on the first resolution.
   */
  configureForNormalMode() {
    this._orientationAdviceLockActive = true;
    this._orientationAdviceSessionSeen = false;
    this._applyConfig({ ...this.defaultConfig, chatInputLayout: 'simple' });
    this.emitStateChanged();
  }

  /**
   * Release the post-orientation simple-input + privacy-button hold and
   * restore both. Triggered the first time the user leaves the
   * post-orientation advice session (new chat / mode switch / page
   * navigation). Re-resolves privacyButton through `_resolvePrivacyButton` so
   * the current mode (e.g. DISCOVERY) is still honored.
   * @private
   */
  _releaseOrientationAdviceLock() {
    this._orientationAdviceLockActive = false;
    this._orientationAdviceSessionSeen = false;
    this.set('chatInputLayout', 'full');

    if (!this._lastConfig) return;
    const previousPrivacyButton = this.state.privacyButton;
    const previousHeaderOptionsMenu = this.state.headerOptionsMenu;
    const next = this._resolvePrivacyButton(this._lastConfig);
    if (next !== previousPrivacyButton) {
      this.state.privacyButton = next;
      this.emitSingleToggle('privacyButton');
    }
    const nextMenu = this._resolveHeaderOptionsMenu(this._lastConfig);
    if (nextMenu !== previousHeaderOptionsMenu) {
      this.state.headerOptionsMenu = nextMenu;
      this.emitSingleToggle('headerOptionsMenu');
    }
  }

  /**
   * Clean up event listeners
   */
  destroy() {
    this.eventBus.off('user:updated', this.handleUserUpdated);
    this.eventBus.off('user:onboarding-step-changed', this.handleUserUpdated);
    this.eventBus.off('mode:changed', this.handleModeChanged);
    this.eventBus.off('mode:restored', this.handleModeRestored);
    this.eventBus.off('mode:cleared', this.handleModeCleared);
    this.eventBus.off('orientation:dismissed', this.handleOrientationDismissed);
    this.eventBus.off(CONTENT_ACTIVATED, this.handleContentActivated);
    console.log('[FeatureToggleService] Destroyed');
  }

  /**
   * Handle mode transitions from ModeOrchestrator. Updates `currentModeName`
   * and runs the full orientation-state reapply. A same-mode re-entry is a
   * no-op (orientation liveness cannot have changed).
   *
   * @param {{ to: string, from: string, data?: Object }} data - mode:changed payload
   */
  handleModeChanged(data) {
    const nextMode = data?.to || null;
    if (nextMode === this.currentModeName) return;
    this.currentModeName = nextMode;
    this._reapplyOrientationState();
  }

  /**
   * Handle mode restoration from ModeOrchestrator (`mode:restored`, emitted by
   * `restoreMode` when returning to an existing session without a new backend
   * session). Updates `currentModeName` and reapplies orientation state so a
   * cross-mode restore (e.g. DISCOVERY → restored ADVICE) doesn't leave toggles
   * stale.
   *
   * @param {{ mode: string }} data - mode:restored payload
   */
  handleModeRestored(data) {
    const nextMode = data?.mode || null;
    if (nextMode === this.currentModeName) return;
    this.currentModeName = nextMode;
    this._reapplyOrientationState();
  }

  /**
   * Handle `mode:cleared` (emitted by `returnToDashboard()` when the
   * orchestrator drops its active mode — including the orientation exit path).
   * Clears `currentModeName` and reapplies orientation state, which is what
   * actually unlocks a user who exited orientation early.
   */
  handleModeCleared() {
    if (this.currentModeName === null) return;
    this.currentModeName = null;
    this._reapplyOrientationState();
  }

  /**
   * Handle `orientation:dismissed` (emitted by `OrientationCloseManager` when
   * the user taps the ✕). Subscribed for symmetry; the real unlock rides the
   * `mode:cleared` that `dismiss()` also fires via `returnToDashboard`. The
   * reapply is idempotent diff-and-emit, so the double-fire is harmless.
   */
  handleOrientationDismissed() {
    this._reapplyOrientationState();
  }

  /**
   * Full orientation-state reapply: recompute every `orientationLive`-derived
   * toggle from the *live* predicate, diff against current state, and emit only
   * the toggles that actually flipped. Run on every mode-lifecycle transition.
   *
   * Boundary (load-bearing): this MUST NOT wholesale-replace `this.state` with a
   * synthesized "full" config, because during the post-orientation advice hold
   * `chatInputLayout` is held at 'simple' (`_orientationAdviceLockActive`) and a
   * synthesized config would carry 'full' and stomp it. So `chatInputLayout` is
   * never touched here — it stays owned by the hold. Each derived toggle is
   * emitted per-field via `set()` (emits only on change), and privacy/header are
   * resolved through the same hold-aware resolvers used elsewhere.
   *
   * `_lastConfig` is refreshed from the live-derived base (preserving the
   * current `chatInputLayout`) so downstream re-resolution — the hold release,
   * later mode changes — reads the correct allowances rather than a stale
   * lockdown config left behind by `mode:cleared`.
   *
   * @private
   */
  _reapplyOrientationState() {
    const orientationLive = this._resolveOrientationLive();
    const onboardingStep = window.app?.currentUser?.onboardingStep;
    const base = orientationLive
      ? this._resolveLockdownConfig(onboardingStep)
      : this.defaultConfig;

    // `chatInputLayout` is owned by the post-orientation advice hold while it is
    // active — preserve it exactly to avoid stomping the 'simple' hold. When the
    // hold is inactive it is orientation-derived like the rest of the chrome, so
    // a dismissing user (not live) gets 'full' and a resuming user (live) 'simple'.
    const nextChatInputLayout = this._orientationAdviceLockActive
      ? this.state.chatInputLayout
      : base.chatInputLayout;

    // Keep the cached base config in sync with the live predicate, carrying the
    // resolved layout so downstream re-resolution reads a consistent value.
    this._lastConfig = { ...base, chatInputLayout: nextChatInputLayout };

    // Orientation-derived chrome. `closeButton` is the orientation exit
    // affordance; `plusButton` is suppressed during orientation.
    this.set('closeButton', base.closeButton);
    this.set('plusButton', base.plusButton);
    this.set('chatInputLayout', nextChatInputLayout);

    // Mode/hold-aware toggles, resolved against the refreshed base.
    this.set('privacyButton', this._resolvePrivacyButton(base));
    this.set('headerOptionsMenu', this._resolveHeaderOptionsMenu(base));

    // Content-navigation visibility is also `orientationLive`-derived, so it is
    // re-resolved and emitted on every mode-lifecycle transition alongside the
    // chrome toggles above.
    this._emitSidebarContent();
  }

  /**
   * Handle `content:activated` (an accepted draft practice flipping to active —
   * `'practice'`-only, see contentSync.js). Durably updates the cached
   * `hasContent.practices` flag so a later re-resolve keeps Practices visible
   * (updating the cached fact, not just emitting, is what makes it stick past
   * the next mode transition), then re-emits content visibility.
   */
  handleContentActivated() {
    const currentUser = window.app?.currentUser;
    if (!currentUser) return;
    currentUser.hasContent = { ...(currentUser.hasContent || {}), practices: true };
    this._emitSidebarContent();
  }

  /**
   * Handle user update events
   * @param {Object} data - Event data containing user info
   */
  handleUserUpdated(data) {
    const user = data.user || data;
    const onboardingStep = user?.onboardingStep;

    // Pick up foundation-completion from the refreshed user object before
    // re-applying step config, so upgradePrompts is resolved with the latest value.
    if (typeof user?.foundationCompleted === 'boolean') {
      this.foundationCompleted = user.foundationCompleted;
    }

    if (onboardingStep !== undefined) {
      this.configureFromOnboardingStep(onboardingStep);
    }

    // A refreshed user object may carry updated `hasContent`/`coupleId` (e.g. a
    // /auth/me refetch), so re-resolve content-navigation visibility from the
    // authoritative `currentUser` the app has already updated.
    this._emitSidebarContent();
  }

  /**
   * Configure all toggle states from an onboarding step
   * This is the primary way to set states based on user progress
   *
   * @param {string|null} step - Current onboarding step
   */
  configureFromOnboardingStep(step) {
    const previousState = { ...this.state };
    // Lockdown vs default keys on the *live* orientation predicate, not the
    // historical `onboardingStep`. A dismissed-but-incomplete user (phase-value
    // step, no longer on screen) resolves not-live and falls through to
    // `defaultConfig` — unlocked. The step only selects *which* lockdown config
    // to apply while orientation is genuinely live.
    const config = this._resolveOrientationLive()
      ? this._resolveLockdownConfig(step)
      : this.defaultConfig;

    this._applyConfig(config);

    // Check if state actually changed
    const hasChanged =
      previousState.privacyButton !== this.state.privacyButton ||
      previousState.headerOptionsMenu !== this.state.headerOptionsMenu ||
      previousState.plusButton !== this.state.plusButton ||
      previousState.chatInputLayout !== this.state.chatInputLayout ||
      previousState.upgradePrompts !== this.state.upgradePrompts ||
      previousState.sidebarHistory !== this.state.sidebarHistory ||
      previousState.closeButton !== this.state.closeButton;

    if (hasChanged) {
      console.log('[FeatureToggleService] State changed from step:', step, previousState, '->', this.state);
      this.emitStateChanged();
    }
  }

  /**
   * Set a specific toggle value
   * Use this for programmatic control outside of onboarding flow
   *
   * @param {keyof FeatureToggleState} toggle - Toggle name
   * @param {*} value - New value for the toggle
   */
  set(toggle, value) {
    if (!(toggle in this.state)) {
      console.warn(`[FeatureToggleService] Unknown toggle: ${toggle}`);
      return;
    }

    if (this.state[toggle] !== value) {
      this.state[toggle] = value;
      console.log(`[FeatureToggleService] Set ${toggle} to:`, value);
      this.emitSingleToggle(toggle);
    }
  }

  /**
   * Get a specific toggle value
   * @param {string} toggle - Toggle name
   * @returns {*} Toggle value
   */
  get(toggle) {
    return this.state[toggle];
  }

  /**
   * Get full current state
   * @returns {FeatureToggleState} Current toggle states
   */
  getState() {
    return { ...this.state };
  }

  /**
   * Emit state change events for all toggles
   * @private
   */
  emitStateChanged() {
    this.eventBus.emit('toggle:privacyButton', { visible: this.state.privacyButton });
    this.eventBus.emit('toggle:headerOptionsMenu', { visible: this.state.headerOptionsMenu });
    this.eventBus.emit('toggle:plusButton', { visible: this.state.plusButton });
    this.eventBus.emit('toggle:chatInputLayout', { layout: this.state.chatInputLayout });
    this.eventBus.emit('toggle:upgradePrompts', { enabled: this.state.upgradePrompts });
    this.eventBus.emit('toggle:sidebarHistory', { visible: this.state.sidebarHistory });
    this.eventBus.emit('toggle:closeButton', { visible: this.state.closeButton });
    this.eventBus.emit('toggle:state-changed', { ...this.state });
  }

  /**
   * Emit state change event for a single toggle
   * @private
   * @param {string} toggle - Toggle name
   */
  emitSingleToggle(toggle) {
    switch (toggle) {
      case 'privacyButton':
        this.eventBus.emit('toggle:privacyButton', { visible: this.state.privacyButton });
        break;
      case 'headerOptionsMenu':
        this.eventBus.emit('toggle:headerOptionsMenu', { visible: this.state.headerOptionsMenu });
        break;
      case 'plusButton':
        this.eventBus.emit('toggle:plusButton', { visible: this.state.plusButton });
        break;
      case 'chatInputLayout':
        this.eventBus.emit('toggle:chatInputLayout', { layout: this.state.chatInputLayout });
        break;
      case 'upgradePrompts':
        this.eventBus.emit('toggle:upgradePrompts', { enabled: this.state.upgradePrompts });
        break;
      case 'sidebarHistory':
        this.eventBus.emit('toggle:sidebarHistory', { visible: this.state.sidebarHistory });
        break;
      case 'closeButton':
        this.eventBus.emit('toggle:closeButton', { visible: this.state.closeButton });
        break;
    }
    this.eventBus.emit('toggle:state-changed', { ...this.state });
  }
}
